import React, { useState, useCallback, useEffect } from 'react';
import {
  Form, Input, Button, Modal, Tag, message,
} from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined,
  LockOutlined,
  EditOutlined, SaveOutlined, CloseOutlined,
  KeyOutlined, ApartmentOutlined,
  ExclamationCircleOutlined,
  EyeOutlined, EyeInvisibleOutlined, BankOutlined,
  TeamOutlined, GlobalOutlined, IdcardOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { CHARTE_COLORS, getUser, setUser as enregistrerSession, libelleProfil } from '../../variables/constants';
import PhoneComponent from '../../components/PhoneComponent';
import CarteAuthenticator from './CarteAuthenticator';

import { Trans, useTranslation } from 'react-i18next';

// ─── Helpers ────────────────────────────────────────────────────────────────

const initials = (user) =>
  `${(user?.prenoms || user?.prenom || ' ')[0]}${(user?.nom || ' ')[0]}`
    .toUpperCase().trim() || 'U';


// ─── Avatar ──────────────────────────────────────────────────────────────────

function Avatar({ user, size = 72 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.28,
      background: `linear-gradient(135deg, ${CHARTE_COLORS.green} 0%, ${CHARTE_COLORS.greenMid} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.32, fontWeight: 700, color: '#fff',
      boxShadow: `0 4px 20px ${CHARTE_COLORS.greenRing}`,
      flexShrink: 0, letterSpacing: '-0.5px', userSelect: 'none',
    }}>
      {initials(user)}
    </div>
  );
}

// ─── InfoRow ─────────────────────────────────────────────────────────────────

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0', borderBottom: `1px solid ${CHARTE_COLORS.border}`,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: CHARTE_COLORS.greenLight,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon style={{ fontSize: 14, color: CHARTE_COLORS.green }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: CHARTE_COLORS.faint, marginBottom: 1 }}>{label}</div>
        <div style={{
          fontSize: 14, color: CHARTE_COLORS.text, fontWeight: 500,
          fontFamily: mono ? 'ui-monospace, monospace' : undefined,
          wordBreak: 'break-all',
        }}>
          {value || <span style={{ color: CHARTE_COLORS.faint, fontStyle: 'italic' }}>-</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function Card({ title, subtitle, icon: Icon, children, action, accent }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${CHARTE_COLORS.border}`,
      borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,.04)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: `1px solid ${CHARTE_COLORS.border}`,
        background: accent
          ? `linear-gradient(90deg, ${CHARTE_COLORS.greenLight} 0%, #fff 60%)`
          : '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {Icon && (
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: CHARTE_COLORS.greenLight,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon style={{ fontSize: 15, color: CHARTE_COLORS.green }} />
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: CHARTE_COLORS.text }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: CHARTE_COLORS.faint, marginTop: 1 }}>{subtitle}</div>}
          </div>
        </div>
        {action}
      </div>
      <div style={{ padding: '4px 20px 20px' }}>{children}</div>
    </div>
  );
}

// ─── ChangePasswordModal ──────────────────────────────────────────────────────
// Appel corrigé : PUT /v1/utilisateurs/{trackingId}/changer-password
// (correspond à l'endpoint réel du UtilisateurController)

function ChangePasswordModal({ open, onClose, trackingId }) {
  const { t } = useTranslation('account');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    try {
      const vals = await form.validateFields();
      setLoading(true);
      await axiosInstance.put(
        `/v1/utilisateurs/${trackingId}/changer-password`,
        {
          ancienMotDePasse: vals.ancien,
          nouveauMotDePasse: vals.nouveau,
        }
      );
      message.success(t('motDePasse.modifie'));
      form.resetFields();
      onClose();

    } catch (err) {
      if (err?.response?.data?.message) {
        message.error(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={loading}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyOutlined style={{ color: CHARTE_COLORS.green }} />
          <span>{t('motDePasse.modifier')}</span>
        </div>
      }
      okText={t('actions.enregistrer')}
      okButtonProps={{ htmlType: 'submit', style: { background: CHARTE_COLORS.green, borderColor: CHARTE_COLORS.green } }}
      cancelText={t('actions.annuler')}
      width={440}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label={t('motDePasse.actuel')}
          name="ancien"
          rules={[{ required: true, message: t('validation.requis') }]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: CHARTE_COLORS.faint }} />}
            iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            placeholder="••••••••"
          />
        </Form.Item>
        <Form.Item
          label={t('motDePasse.nouveau')}
          name="nouveau"
          rules={[
            { required: true, message: t('validation.requis') },
            { min: 8, message: t('motDePasse.minimum') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: CHARTE_COLORS.faint }} />}
            iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            placeholder={t('motDePasse.placeholderNouveau')}
          />
        </Form.Item>
        <Form.Item
          label={t('motDePasse.confirmer')}
          name="confirm"
          dependencies={['nouveau']}
          rules={[
            { required: true, message: t('validation.requis') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('nouveau') === value) return Promise.resolve();
                return Promise.reject(new Error(t('motDePasse.differents')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: CHARTE_COLORS.faint }} />}
            iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            placeholder={t('motDePasse.placeholderConfirmer')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// UserProfileSection
function UserProfileSection({ user, onRefresh }) {
  const { t } = useTranslation('account');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwdModal, setPwdModal] = useState(false);
  const [form] = Form.useForm();

  const startEdit = () => {
    form.setFieldsValue({
      nom: user?.nom || '',
      prenoms: user?.prenoms || '',
      telephone: user?.telephone || '',
    });
    setEditing(true);
  };

  const cancelEdit = () => { form.resetFields(); setEditing(false); };

  const saveEdit = async () => {
    try {
      const vals = await form.validateFields();
      setLoading(true);
      // /me et non /{trackingId} : la route par identifiant est une route
      // d'administration (UTILISATEUR_UPDATE) et exige le corps complet du compte.
      // Corriger son propre nom n'est pas un acte d'administration.
      await axiosInstance.put(
        '/v1/utilisateurs/me',
        { nom: vals.nom, prenoms: vals.prenoms, telephone: vals.telephone }
      );
      message.success(t('informations.misAJour'));
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      if (err?.response?.data?.message) message.error(err.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card
        title={t('informations.titre')}
        subtitle={t('informations.sousTitre')}
        icon={UserOutlined}
        accent
        action={
          editing ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit} style={{ borderRadius: 6 }}>
                {t('actions.annuler')}
              </Button>
              <Button
                size="small" icon={<SaveOutlined />} type="primary"
                loading={loading} onClick={saveEdit}
                style={{ borderRadius: 6, background: CHARTE_COLORS.green, borderColor: CHARTE_COLORS.green }}
              >
                {t('actions.enregistrer')}
              </Button>
            </div>
          ) : (
            <Button
              size="small" icon={<EditOutlined />} onClick={startEdit}
              style={{ borderRadius: 6, borderColor: CHARTE_COLORS.green, color: CHARTE_COLORS.green }}
            >
              {t('actions.modifier')}
            </Button>
          )
        }
      >
        {/* Hero */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 18,
          padding: '18px 0 14px', borderBottom: `1px solid ${CHARTE_COLORS.border}`, marginBottom: 4,
        }}>
          <Avatar user={user} size={64} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: CHARTE_COLORS.text, lineHeight: 1.2 }}>
              {user?.prenoms} {user?.nom}
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Tag color="green" style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}>
                {libelleProfil(user)}
              </Tag>
              <Tag style={{
                borderRadius: 20, fontSize: 11,
                background: CHARTE_COLORS.blueBg, color: CHARTE_COLORS.blue,
                border: `1px solid ${CHARTE_COLORS.blue}22`,
              }}>
                {user?.organismeRaisonSociale || '-'}
              </Tag>
            </div>
          </div>
        </div>

        {/* Lecture */}
        {!editing && (
          <>
            <InfoRow icon={MailOutlined} label={t('informations.email')} value={user?.email} />
            <InfoRow icon={IdcardOutlined} label={t('informations.identifiant')} value={user?.username} mono />
            <InfoRow icon={PhoneOutlined} label={t('informations.telephone')} value={user?.telephone} />
            <InfoRow icon={ApartmentOutlined} label={t('informations.profilAcces')} value={libelleProfil(user)} />
            <InfoRow icon={BankOutlined} label={t('informations.organisme')} value={user?.organismeRaisonSociale} />
            <div style={{ marginTop: 16 }}>
              <Button
                icon={<KeyOutlined />}
                onClick={() => setPwdModal(true)}
                style={{ borderRadius: 8, borderColor: CHARTE_COLORS.border, color: CHARTE_COLORS.muted, fontSize: 13 }}
              >
                {t('motDePasse.modifier')}
              </Button>
            </div>
          </>
        )}

        {/* Édition */}
        {editing && (
          <Form form={form} layout="vertical" style={{ marginTop: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item label={t('informations.nom')} name="nom" rules={[{ required: true, message: t('validation.requis') }]}>
                <Input
                  prefix={<UserOutlined style={{ color: CHARTE_COLORS.faint }} />}
                  placeholder={t('informations.placeholderNom')} style={{ borderRadius: 8 }}
                />
              </Form.Item>
              <Form.Item label={t('informations.prenoms')} name="prenoms" rules={[{ required: true, message: t('validation.requis') }]}>
                <Input
                  prefix={<UserOutlined style={{ color: CHARTE_COLORS.faint }} />}
                  placeholder={t('informations.placeholderPrenoms')} style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </div>
            <Form.Item label={t('informations.telephone')} name="telephone">
              <PhoneComponent prefix="+228" maxLength={8} />
            </Form.Item>
            <div style={{
              padding: '10px 12px', borderRadius: 8,
              background: CHARTE_COLORS.greenLight,
              fontSize: 12, color: CHARTE_COLORS.green,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <ExclamationCircleOutlined />
              {t('informations.reserveAdministrateur')}
            </div>
          </Form>
        )}
      </Card>

      <ChangePasswordModal
        open={pwdModal}
        onClose={() => setPwdModal(false)}
        trackingId={user?.utilisateurTrackingId}
      />
    </>
  );
}

// OrgInfoSection
function OrgInfoSection({ user }) {
  const { t } = useTranslation('account');
  return (
    <Card title={t('organisme.titre')} subtitle={t('organisme.sousTitre')} icon={BankOutlined}>
      <InfoRow icon={BankOutlined} label={t('organisme.raisonSociale')} value={user?.organismeRaisonSociale} />
      <InfoRow icon={IdcardOutlined} label={t('organisme.code')} value={user?.organismeCode} mono />
      <InfoRow icon={GlobalOutlined} label={t('organisme.pays')} value={user?.organismeCodePays} />
      <InfoRow icon={MailOutlined} label={t('organisme.email')} value={user?.organismeEmail} />
      <InfoRow icon={TeamOutlined} label={t('organisme.responsable')} value={user?.organismeResponsable} />
    </Card>
  );
}

// Page────────────────────────────────────────────────────────────────────

export default function Profil() {
  const { t } = useTranslation('account');
  const [user, setUser] = useState(getUser());

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/v1/utilisateurs/me');
      setUser(data.data);
      // Surtout PAS sessionStorage.setItem directement : cette réponse ne porte
      // pas les habilitations, et les écrire telles quelles retirait ses droits
      // d'écran à qui venait simplement consulter sa fiche. `setUser` préserve
      // ce que la charge n'apporte pas.
      enregistrerSession(data.data);
    } catch (err) {
      console.error('Impossible de charger le profil', err);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  return (
    <Layout title={t('page.titre')}>
      <div className="cb-page-header" style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar user={user} size={48} />
          <div>
            <h1 className="cb-page-title" style={{ margin: 0 }}>
              {user?.prenoms} {user?.nom}
            </h1>
            <p className="cb-page-sub" style={{ margin: '2px 0 0' }}>
              {libelleProfil(user)}
              {user?.organismeRaisonSociale ? ` · ${user.organismeRaisonSociale}` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Deux colonnes pour tout le monde, et non plus pour les seuls
          administrateurs : la carte de l'application d'authentification occupe
          desormais la colonne de droite, en vis-a-vis des informations
          personnelles. `minmax(0, 1fr)` et non `1fr` - une grille laisse sinon
          ses colonnes deborder sur le contenu large plutot que le contraindre. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 20,
        alignItems: 'start',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <UserProfileSection user={user} onRefresh={fetchMe} />
          <OrgInfoSection user={user} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Association d'une application d'authentification. Elle n'etait
              atteignable qu'au vol - une seule fois, juste apres un code recu par
              courriel - et le retrait n'existait nulle part. Ici, l'agent decide
              quand. */}
          <CarteAuthenticator />
        </div>
      </div>
    </Layout>
  );
}